import { IToken, Permission } from '@dokucoach/common-types';
import RefreshTokenModal from 'components/AuthManager/RefreshTokenModal';
import routePaths from 'constants/route-paths';
import { useAuth } from 'hooks/auth';
import React, { createContext, ReactNode, useContext } from 'react';
import { TimeosConfig, User } from 'services/auth';
import { getPath } from 'utils/path';

type RouteType = {
  publicPath: string;
  privatePath: string;
};

type Props = {
  publicPath: string;
  privatePaths: { path: string; permissions: Permission[] }[];
  children: ReactNode;
};

type ContextType = {
  timeosConfig: TimeosConfig | null;
  user: User | null;
  isLoggedIn: boolean;
  hasPermission: (p: Permission) => boolean;
  hasOnePermission: (p: Permission[]) => boolean;
  fetchUser: (token: IToken) => void;
  logout: () => void;
} & RouteType;

export const AuthManagerContext = createContext({} as ContextType);

const AuthManager = ({ children, publicPath, privatePaths }: Props) => {
  const { isLoggedIn, fetchUser, user, timeosConfig, isLoading, logout } = useAuth();

  const hasPermission = (permission: Permission): boolean => {
    if (!user?.rights) return false;
    return user.rights.includes(permission.key);
  };

  const hasOnePermission = (permissions: Permission[]): boolean => {
    if (!user?.rights) return false;
    return permissions.some((value: Permission) => user.rights?.includes(value.key));
  };

  const getFirstAllowedPath = (): string => {
    const index = privatePaths.findIndex(({ permissions }) => hasOnePermission(permissions));
    if (index === -1) {
      // FIXME: redirect to e.g. /home once we have a default landing apge
      // for now: reidrect to the login page, if we cannot find any matching route
      return routePaths.LOGIN;
    } else {
      return privatePaths[index].path;
    }
  };

  return (
    <AuthManagerContext.Provider
      value={{
        user,
        isLoggedIn,
        fetchUser,
        timeosConfig,
        publicPath: getPath(publicPath),
        privatePath: getPath(getFirstAllowedPath()),
        hasPermission,
        hasOnePermission,
        logout,
      }}
    >
      {!isLoading && children}
      <RefreshTokenModal isActive={isLoggedIn} />
    </AuthManagerContext.Provider>
  );
};

export const useContextAuthManager = () => {
  return useContext(AuthManagerContext);
};

export default AuthManager;
